import { UserLocation } from '../../_UserLocation';
import { DEBUG_MODE, IS_PRODUCTION_DOMAIN } from '../../_DebugConfig';
import { debug } from '../../_DebugLog';
import MAP_DATA from '../../../_json/_map_data.json';
import { STAMPCARD_RADIUS } from '../../map/MapConstants';

class ProximityManager
{
    constructor()
    {
        this.proximityUpdateCallback = null;
    }

    setProximityUpdateCallback(callback)
    {
        this.proximityUpdateCallback = callback;
    }

    getCurrentPositionFallback(items, onSuccess, onError)
    {
        navigator.geolocation.getCurrentPosition(
            (position) =>
            {
                const userLocation = UserLocation.getInstance();
                
                // Update current position so checkStampItemProximity can use it
                userLocation.currentPosition = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                };
                
                const result = this.checkProximityForItems(items, position);
                onSuccess(result);
            },
            (error) =>
            {
                debug.log('Error getting location:', error);
                const errorResult = this.createErrorResult(items);
                onError(errorResult);
            }
        );
    }

    createErrorResult(items)
    {
        // Handle both single item and array of items
        const itemsArray = Array.isArray(items) ? items : [items];
        
        if (itemsArray.length === 1)
        {
            // Single item - return boolean
            return false;
        }
        else
        {
            // Multiple items - return object with item IDs as keys
            const disabledItems = {};
            itemsArray.forEach(item => disabledItems[item.id] = false);
            return disabledItems;
        }
    }

    checkProximityForItems(items, currentPosition = null)
    {
        // Handle both single item and array of items
        const itemsArray = Array.isArray(items) ? items : [items];
        const isSingleItem = !Array.isArray(items);
        
        if (!itemsArray?.length) return isSingleItem ? false : {};

        const userLocation = UserLocation.getInstance();

        // If we don't have a position at all, disable all items
        if (!currentPosition && !userLocation.getCurrentPosition())
        {
            return this.createErrorResult(items);
        }

        // If we have a position (either cached or newly fetched), use it
        if (currentPosition)
        {
            userLocation.currentPosition = currentPosition;
        }

        // Process all items with the current position
        const results = {};
        let singleResult = false;

        itemsArray.forEach(item =>
        {
            // Find building data in MAP_DATA instead of STAMPCARD_DATA
            const buildingItem = MAP_DATA.buildings?.find(building => building.id === item.id);
            if (!buildingItem)
            {
                if (isSingleItem)
                {
                    singleResult = false;
                }
                else
                {
                    results[item.id] = false;
                }
                return;
            }

            const radius = parseFloat(buildingItem.sc_radius || STAMPCARD_RADIUS);
            const isInRange = userLocation.checkStampItemProximity(buildingItem, radius);
            
            if (isSingleItem)
            {
                singleResult = isInRange;
            }
            else
            {
                results[item.id] = isInRange;
            }
        });

        return isSingleItem ? singleResult : results;
    }

    checkStampProximity(items, gpsCheckEnabled, onUpdate)
    {
        // Handle both single item and array of items
        const itemsArray = Array.isArray(items) ? items : [items];
        const isSingleItem = !Array.isArray(items);
        
        if (!itemsArray?.length) return;

        const isDebugMode = !IS_PRODUCTION_DOMAIN && DEBUG_MODE;

        // If not in DEBUG_MODE, always check GPS regardless of gpsCheckEnabled setting
        // If in DEBUG_MODE, respect the gpsCheckEnabled setting
        if (!isDebugMode || !gpsCheckEnabled)
        {
            if (!isDebugMode)
            {
                // Continue with GPS check in production mode
            }
            else
            {
                // In debug mode with GPS check disabled
                if (isSingleItem)
                {
                    onUpdate(true);
                }
                else
                {
                    const enabledItems = {};
                    itemsArray.forEach(item => enabledItems[item.id] = true);
                    onUpdate(enabledItems);
                }
                return;
            }
        }

        if (isSingleItem)
        {
            debug.log('Checking GPS proximity for ' + (items.name || 'building ID ' + items.id));
        }
        else
        {
            debug.log('Checking GPS proximity for all AR items');
        }

        const userLocation = UserLocation.getInstance();
        const currentPosition = userLocation.getCurrentPosition();

        if (currentPosition)
        {
            const result = this.checkProximityForItems(items);
            onUpdate(result);

            // If position is not being updated, we can do a fresh check as backup
            if (!userLocation.isPositionUpdating())
            {
                // For single items, only do fallback if not in range
                if (isSingleItem && !result)
                {
                    this.getCurrentPositionFallback(items, onUpdate, onUpdate);
                }
                else if (!isSingleItem)
                {
                    this.getCurrentPositionFallback(items, onUpdate, onUpdate);
                }
            }
        }
        else
        {
            // Get position only once for all items if no cached position exists
            this.getCurrentPositionFallback(items, onUpdate, onUpdate);
        }
    }

    setupProximityUpdates(items, gpsCheckEnabled, onUpdate)
    {
        // Handle both single item and array of items
        const itemsArray = Array.isArray(items) ? items : [items];
        const isSingleItem = !Array.isArray(items);
        
        // For single items, check if it has AR capability
        if (isSingleItem && !items?.ar) return;
        
        // For multiple items, filter only AR-enabled items
        if (!isSingleItem)
        {
            const arEnabledItems = itemsArray.filter(item => item.ar);
            if (!arEnabledItems.length) return;
        }
        
        // Initial check
        this.checkStampProximity(items, gpsCheckEnabled, onUpdate);

        // Set up proximity update callback
        const userLocation = UserLocation.getInstance();
        const handleProximityUpdate = () => this.checkStampProximity(items, gpsCheckEnabled, onUpdate);
        userLocation.setProximityUpdateCallback(handleProximityUpdate);

        return () =>
        {
            // Clean up callback when component unmounts
            userLocation.setProximityUpdateCallback(null);
        };
    }
}

export default ProximityManager; 